<script setup lang="ts">
import { useLocalStorage } from '@vueuse/core'

import type { ScoreboardSyncState } from '~/stores/scoreboard'

import ScoreboardSetupScreen from '~/components/Scoreboard/SetupScreen.vue'
import { GAME_REGISTRY } from '~/utils/gameRegistry'

definePageMeta({
  layout: 'games',
  prerender: true,
})

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const store = useScoreboardStore()
const toast = useToast()
const { trackEvent } = useAnalytics()

// Defer view-mode detection to post-mount so the prerendered HTML and
// the initial hydration tree always agree.
const mounted = ref(false)
onMounted(() => {
  mounted.value = true
})

const viewMode = computed<'controller' | 'presenter'>(() => {
  if (!mounted.value) {
    return 'controller'
  }
  return route.query.view === 'presenter' ? 'presenter' : 'controller'
})
const isControllerView = computed(() => viewMode.value === 'controller')
const isPresenterView = computed(() => viewMode.value === 'presenter')

const {
  connected: presentationConnected,
  connecting: presentationConnecting,
  connectPresentationAsWindow,
  disconnectPeerPresenters,
  disconnectPresentation,
  lastError: presentationError,
  pairingCode,
  pairingUrl,
  peerPresentersCount,
  peerReady,
  peerTransportConnecting,
  popupPresenterConnected,
  presentationSupported,
  presenterEntryUrl,
  presenterLeave,
  presenterReconnect,
  presenterTargetCode,
  presenterTransportKind,
  regeneratePairingCode,
} = useGamePresentation<ScoreboardSyncState>({
  peerNamespace: GAME_REGISTRY.scoreboard.peerNamespace,
  presenterChar: GAME_REGISTRY.scoreboard.presenterChar,
  store: {
    applySyncState: state => store.applySyncState(state),
    getSyncState: () => store.getSyncState(),
  },
  viewMode,
})

// ── Setup screen ref (for header menu actions) ─────────
const setupRef = ref<InstanceType<typeof ScoreboardSetupScreen> | null>(null)

async function handleConnectPresentationWindow() {
  if (!presentationSupported.value) {
    toast.add({
      color: 'warning',
      description: gpUi.value?.unsupported,
      duration: 2800,
      icon: 'mdi:alert-circle-outline',
    })
    return
  }

  const opened = await connectPresentationAsWindow()
  if (opened) {
    return
  }

  if (presentationError.value) {
    toast.add({
      color: 'error',
      description: gpUi.value?.connectionFailed,
      duration: 3200,
      icon: 'mdi:alert-circle-outline',
    })
    return
  }

  toast.add({
    color: 'error',
    description: gpUi.value?.connectionFailed,
    duration: 3200,
    icon: 'mdi:alert-circle-outline',
  })
}

function handleDisconnectPeerPresenters() {
  disconnectPeerPresenters()
  toast.add({
    color: 'neutral',
    description: gpUi.value?.peerDisconnected,
    duration: 2200,
    icon: 'mdi:account-off-outline',
  })
}

function handleDisconnectPresentation() {
  disconnectPresentation()
}

function handleLaunchDisplayMode() {
  router.push({ query: { ...route.query, view: 'presenter' } })
}

function handlePresenterLeave() {
  presenterLeave()
}

function handleRegeneratePairingCode() {
  regeneratePairingCode()
  toast.add({
    color: 'neutral',
    description: gpUi.value?.pairingRegenerated,
    duration: 2000,
    icon: 'mdi:refresh',
  })
}

function handleReset() {
  setupRef.value?.handleReset()
}

// ── Load all content ──────────────────────────────────────
const [ { data: ui }, { data: gpUi } ] = await Promise.all([
  useScoreboardUI(),
  useGamePresentationUI(),
])

watch(presentationConnected, (isNowConnected, wasConnected) => {
  if (!isControllerView.value || !wasConnected || isNowConnected) {
    return
  }
  toast.add({
    color: 'warning',
    description: gpUi.value?.disconnectedStatus,
    duration: 2500,
    icon: 'mdi:monitor-off',
  })
})

const seo = computed(() => ui.value?.page.seo)
const seoTitle = computed(() => seo.value?.title ?? ui.value?.page.title)
const seoDescription = computed(() => seo.value?.description ?? ui.value?.page.description)
const ogTitle = computed(() => seo.value?.ogTitle ?? seoTitle.value)
const ogDescription = computed(() => seo.value?.ogDescription ?? seoDescription.value)

useSeoMeta({
  description: () => seoDescription.value,
  ogDescription: () => ogDescription.value,
  ogTitle: () => ogTitle.value,
  title: () => seoTitle.value,
  twitterDescription: () => ogDescription.value,
  twitterTitle: () => ogTitle.value,
})

defineOgImageComponent('Default', {
  description: () => ogDescription.value ?? '',
  eyebrow: 'signalfire.games / scoreboard',
  title: () => ogTitle.value ?? 'Scoreboard',
})

// ── URL-driven game phase resume ──────────────────────────
// Same pattern as Showdown / Sword Drill: persist sync state when
// playing, and use ?phase=playing to drive forward/back/reload behavior.
const persistedSyncState = useLocalStorage<null | ScoreboardSyncState>(
  'sfg.scoreboard.gameState',
  null,
  { serializer: { read: v => v ? JSON.parse(v) : null, write: v => JSON.stringify(v) } },
)

function restoreFromPersistedIfAny() {
  if (!isControllerView.value) {
    return
  }
  if (route.query.phase === 'playing' && persistedSyncState.value && store.phase === 'setup') {
    store.applySyncState(persistedSyncState.value)
  }
}

if (import.meta.client) {
  watch(() => store.getSyncState(), state => {
    if (isControllerView.value && state.phase !== 'setup') {
      persistedSyncState.value = state
    }
  }, { deep: true })

  watch(() => route.query.phase, q => {
    if (!isControllerView.value) {
      return
    }
    if (q === 'playing') {
      if (persistedSyncState.value && store.phase === 'setup') {
        store.applySyncState(persistedSyncState.value)
      }
    } else if (store.phase !== 'setup') {
      store.exitGame()
    }
  })

  onMounted(() => {
    restoreFromPersistedIfAny()
  })
}

// ── Game reset hook ────────────────────────────────────
const { register: registerReset } = useGameReset()
registerReset(() => {
  if (import.meta.client && document.fullscreenElement) {
    document.exitFullscreen().catch(() => {})
  }
  store.exitGame()
  if (route.query.phase) {
    void router.replace({ query: { ...route.query, phase: undefined } })
  }
})

// ── Take control: promote presenter to controller in this tab ──
function takeControl() {
  if (!isPresenterView.value || presenterTransportKind.value !== 'peer') {
    return
  }
  persistedSyncState.value = store.getSyncState()
  const next = { ...route.query } as Record<string, string>
  delete next.view
  delete next.code
  delete next.session
  delete next.autofs
  next.phase = 'playing'
  void router.replace({ query: next })
}

// ── Presence context (drives GameFab's connection / status / exit UI) ──
provideGamePresence({
  connected: presentationConnected,
  connecting: peerTransportConnecting,
  lastError: presentationError,
  pairingCode,
  peerPresentersCount,
  presenterTargetCode,
  presenterTransportKind,
  retryConnection: presenterReconnect,
  takeControl,
  viewMode,
})

// ── Game lifecycle ───────────────────────────────────────
function handleStart() {
  if (!isControllerView.value) {
    return
  }

  // Match Showdown / Sword Drill: request fullscreen synchronously
  // (in user-gesture context) unless dev or a presentation window
  // is already connected.
  if (!presentationConnected.value && import.meta.client && !import.meta.dev) {
    document.documentElement.requestFullscreen().catch(() => {})
  }
  store.startGame()
  void router.push({ query: { ...route.query, phase: 'playing' } })
  trackEvent('game_started', {
    auto_stop_on_score: store.config.autoStopOnScore,
    game: 'scoreboard',
    team_count: store.teams.length,
    team_structure: store.config.teamStructure,
    timer_duration_seconds: store.config.timerMode === 'countdown' ? store.config.timerDurationSeconds : null,
    timer_mode: store.config.timerMode,
  })
}
</script>

<template>
  <div
    v-if="isPresenterView"
    class="h-screen w-screen bg-default text-default"
  >
    <GamePresentationPresenterPairing v-if="presenterTransportKind === 'unpaired'"/>

    <ScoreboardGameScreen
      v-else-if="store.phase !== 'setup'"
      :interactive="false"
    />
    <div
      v-else
      class="flex h-full items-center justify-center px-6"
    >
      <div class="w-full max-w-3xl">
        <UCard :ui="{ body: 'bg-muted/5 text-center' }">
          <div class="flex flex-col items-center gap-5 py-6 sm:py-8">
            <UIcon
              class="size-14 text-primary"
              :name="presentationConnected ? 'mdi:check-circle-outline' : 'mdi:progress-clock'"
            />
            <h1 class="text-balance text-3xl font-black tracking-tight sm:text-4xl">
              {{ presentationConnected ? gpUi?.presenterWaitingTitle : gpUi?.presenterConnectingTitle }}
            </h1>
            <p class="max-w-2xl text-balance text-base text-muted sm:text-lg">
              {{ presentationConnected ? gpUi?.presenterWaitingDescription : gpUi?.presenterConnectingDescription }}
            </p>
          </div>
        </UCard>
      </div>
    </div>

    <GamePresentationPresenterFullscreenOverlay v-if="presenterTransportKind === 'peer' && presentationConnected"/>

    <!-- Pairing/setup screens get the prominent SfgChromeButton in the
         top-left. Once the room is in playing mode, the GameFab in the
         bottom-left corner provides the leave action so the
         presentation surface stays clean. -->
    <SfgChromeButton
      v-if="presenterTransportKind === 'unpaired' || store.phase === 'setup'"
      class="fixed top-4 left-4 z-40"
      :icon-left="presenterTransportKind === 'broadcast' ? 'mdi:close' : 'mdi:arrow-left'"
      @click="handlePresenterLeave"
    >
      {{ presenterTransportKind === 'broadcast' ? gpUi?.presenterCloseButton : gpUi?.presenterLeaveButton }}
    </SfgChromeButton>
  </div>

  <!-- Setup screen -->
  <div
    v-else-if="store.phase === 'setup'"
    class="flex min-h-screen flex-col items-center justify-start p-6 xl:justify-center"
  >
    <SfgChromeButton
      to="/games"
      icon-left="mdi:arrow-left"
      class="self-start mb-4 z-30 xl:fixed xl:top-4 xl:left-4 xl:mb-0"
    >
      {{ t('setupMenu.backToLibrary') }}
    </SfgChromeButton>
    <div class="w-full max-w-3xl">
      <SfgGameSetupCard
        icon="twemoji:bar-chart"
        :title="ui?.page.title ?? ''"
        :description="ui?.page.description ?? ''"
        :loading-text="ui?.setup.loading"
        :reset="handleReset"
      >
        <ScoreboardSetupScreen
          ref="setupRef"
          :pairing-code="pairingCode"
          :pairing-url="pairingUrl"
          :peer-presenters-count="peerPresentersCount"
          :peer-ready="peerReady"
          :popup-presenter-connected="popupPresenterConnected"
          :presentation-connecting="presentationConnecting"
          :presentation-supported="presentationSupported"
          :presenter-entry-url="presenterEntryUrl"
          @start="handleStart"
          @connect-presentation-window="handleConnectPresentationWindow"
          @disconnect-peer-presenters="handleDisconnectPeerPresenters"
          @disconnect-presentation="handleDisconnectPresentation"
          @launch-display-mode="handleLaunchDisplayMode"
          @regenerate-pairing-code="handleRegeneratePairingCode"
        />
      </SfgGameSetupCard>
    </div>
  </div>

  <!-- Game screen, fullscreen, no site chrome -->
  <ScoreboardGameScreen
    v-else
    :interactive="true"
    :presentation-connected="presentationConnected"
  />

  <GameFab v-if="store.phase !== 'setup'"/>

  <!-- Help modal: opened from the setup-screen kebab menu and the
       in-game FAB. Mounted at the page root so it's reachable from
       both the setup and game phases without re-mounting. -->
  <GameHelpModal
    :title="ui?.help.title ?? 'Help'"
    :sections="ui?.help.sections ?? []"
  />
</template>
